/**
 * ComputeTierBadge — visual badge for an algorithm's compute tier.
 * Phase 4.8-C — WQ-201
 *
 * Tiers ladder from "no badge" (CPU_NATIVE — every classical algo) to
 * red GPU_REQUIRED. Tooltip explains the tier when present.
 */
import React from 'react';
import type { ComputeTierValue } from '../../services/computeCapabilityService';

const TIER_DISPLAY: Record<
  ComputeTierValue,
  { label: string; color: string; bg: string; description: string } | null
> = {
  CPU_NATIVE: null,
  CPU_VIABLE: {
    label: 'CPU',
    color: '#475569',
    bg: '#e2e8f0',
    description: 'CPU training is viable within the algorithm row threshold.',
  },
  CPU_INFERENCE_GPU_TRAINING: {
    label: 'ONNX inference',
    color: '#0369a1',
    bg: '#dbeafe',
    description:
      'Predictions run fast on CPU via ONNX. Training requires GPU for production data sizes.',
  },
  GPU_STRONGLY_PREFERRED: {
    label: 'GPU recommended',
    color: '#a16207',
    bg: '#fef3c7',
    description:
      'Workable on CPU only for toy datasets. GPU strongly preferred for real workloads.',
  },
  GPU_REQUIRED: {
    label: 'GPU required',
    color: '#b91c1c',
    bg: '#fee2e2',
    description: 'Infeasible on CPU at any meaningful scale. GPU is required.',
  },
};

interface ComputeTierBadgeProps {
  tier: ComputeTierValue;
  size?: 'sm' | 'md';
}

export const ComputeTierBadge: React.FC<ComputeTierBadgeProps> = ({
  tier,
  size = 'sm',
}) => {
  const cfg = TIER_DISPLAY[tier];
  if (!cfg) return null;
  const fontSize = size === 'sm' ? 11 : 12;
  return (
    <span
      title={cfg.description}
      data-testid={`compute-tier-badge-${tier}`}
      style={{
        display: 'inline-block',
        fontSize,
        padding: '2px 8px',
        borderRadius: 10,
        background: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.label}
    </span>
  );
};

export default ComputeTierBadge;
